import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Seo from "@/components/Seo";
import { SITE, localBusinessSchema, breadcrumbSchema } from "@/data/site";
import { MaskedLines, Reveal, StaggerGroup, StaggerItem } from "@/components/motion";
import { Section, SectionHead, EditorialMarquee, CtaBand } from "@/components/sections";
import { IconBadge, BirdMark } from "@/components/brand";
import { GoldButton, WhatsAppButton, WhatsAppButtonLight } from "@/components/cta";
import {
  Clock, RefreshCw, PenSquare, BadgeCheck, Smartphone, MessageCircle, HeartHandshake,
  GraduationCap, BedDouble, ShoppingCart, Globe2, Zap, ShieldCheck, Scale, Target, Sparkles, LifeBuoy, ArrowRight,
} from "lucide-react";

const problems = [
  { icon: Clock, title: "Slow & expensive", text: "Traditional agencies take months and cost a fortune." },
  { icon: RefreshCw, title: "Or a risky gamble", text: "A cheap freelancer who disappears halfway through." },
  { icon: PenSquare, title: "No clear process", text: "Vague scope, endless changes, a project that never ends." },
];

const promises = [
  { icon: BadgeCheck, title: "Fixed price", text: "You know the cost before we start. No surprises." },
  { icon: Smartphone, title: "Mobile-first", text: "Built for how Ghanaians actually browse: on their phones." },
  { icon: MessageCircle, title: "WhatsApp & Mobile Money", text: "Chat and MoMo payments built in from day one." },
  { icon: HeartHandshake, title: "We stay with you", text: "We host, secure and maintain your site after launch." },
];

const audiences = [
  { icon: GraduationCap, title: "Schools", text: "Nurseries, Montessori, private schools & training centres.", to: "/schools" },
  { icon: BedDouble, title: "Hotels", text: "Guest houses, small hotels, resorts & serviced apartments.", to: "/hotels" },
  { icon: ShoppingCart, title: "Shops", text: "Retailers ready to sell online with Mobile Money & card.", to: "/services" },
  { icon: Globe2, title: "Businesses", text: "Growing Ghanaian SMEs that need a professional presence.", to: "/services" },
];

const steps = [
  { n: "01", title: "Brief & plan", text: "We agree scope, price and content. A small booking reserves your sprint." },
  { n: "02", title: "We build (weeks 1–2)", text: "We build your first version and put it online on a private link." },
  { n: "03", title: "You review once", text: "You send all your feedback together, within two weeks. We make the agreed changes." },
  { n: "04", title: "Launch (weeks 3–4)", text: "You approve, we connect your domain, go live, and train your team." },
];

const reasons = [
  { icon: Zap, title: "Fast", text: "Live in four weeks, not months." },
  { icon: ShieldCheck, title: "Reliable", text: "A clear process and a team that stays." },
  { icon: Scale, title: "Fair value", text: "Affordable, with fixed and honest pricing." },
  { icon: Target, title: "Specialist", text: "We know schools, hotels and Ghanaian business." },
  { icon: Sparkles, title: "Passionate", text: "We care about your business succeeding." },
  { icon: LifeBuoy, title: "Supported", text: "Hosted, secured and maintained after launch." },
];

const manifesto = [
  { n: "01", title: "Learn from the past", text: "The Sankofa bird looks back to move forward. We start every project by understanding your business, not by opening a template." },
  { n: "02", title: "Build for the phone first", text: "Most Ghanaian visitors arrive on a phone, often on variable data. We build light, fast pages that load and convert on any connection." },
  { n: "03", title: "Stay after launch", text: "A website is a living thing. We host, secure and maintain what we build, so it keeps working long after you go live." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <>
      <Seo
        title="Sankofa Launch Studio — Website Design in Ghana for Schools, Hotels & Businesses"
        description="Sankofa Launch Studio builds fast, reliable, mobile-first websites, online shops and web apps for Ghanaian schools, hotels and businesses. Fixed prices from GHS 2,200, live in four weeks."
        jsonLd={[localBusinessSchema(), breadcrumbSchema([{ name: "Home", path: "/" }])]}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[92vh] items-center overflow-hidden bg-navy pt-28">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1739298061740-5ed03045b280?crop=entropy&cs=srgb&fm=jpg&q=70&w=1600&ixlib=rb-4.1.0"
            alt="Ghanaian business team meeting around a table in a bright modern office"
            className="h-full w-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-navy-dark/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-dark via-navy-dark/90 to-navy/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/90 via-transparent to-transparent" />
        </motion.div>

        <div className="pointer-events-none absolute -right-16 top-16 h-72 w-72 rounded-full bg-slate/30" />
        <div className="pointer-events-none absolute right-24 top-40 h-24 w-24 rounded-full bg-gold/80" />

        <div className="relative mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-10 px-6 py-20 md:px-12">
          <div className="max-w-4xl">
            <Reveal mount>
              <div className="mb-8 flex items-center gap-3">
                <BirdMark className="h-10 w-10" alt="Sankofa Launch Studio emblem" />
                <span className="font-sans text-xs uppercase tracking-[0.24em] text-gold">
                  Ghanaian digital agency
                </span>
              </div>
            </Reveal>

            <h1 className="font-serif text-5xl font-600 leading-[1.02] tracking-tight text-white md:text-7xl">
              <MaskedLines lines={["Websites that work", "for your business"]} delay={0.15} />
            </h1>

            <Reveal mount delay={0.5}>
              <p className="mt-7 max-w-2xl text-lg leading-relaxed text-white/80 md:text-xl">
                Fast, reliable, mobile-first websites, shops and booking sites for Ghanaian schools,
                hotels and businesses — live in four weeks.
              </p>
            </Reveal>

            <Reveal mount delay={0.62}>
              <div className="mt-9 flex flex-col gap-4 sm:flex-row">
                <GoldButton size="lg" testid="hero-book-btn" />
                <WhatsAppButton size="lg" testid="hero-whatsapp-btn" />
              </div>
            </Reveal>

            <Reveal mount delay={0.72}>
              <p className="mt-8 font-sans text-sm tracking-wide text-white/60">
                Built in Ghana · Mobile-first · WhatsApp &amp; Mobile Money ready
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THE PROBLEM WE SOLVE */}
      <Section className="bg-cream">
        <SectionHead
          eyebrow="The problem we solve"
          title="Getting a good website online is harder than it should be."
          bird
        />
        <StaggerGroup className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {problems.map((p) => (
            <StaggerItem key={p.title}>
              <article className="h-full rounded-2xl bg-white p-8 shadow-soft transition-transform duration-300 hover:-translate-y-1 md:p-10" data-testid={`problem-card-${p.title.split(" ")[0].toLowerCase()}`}>
                <IconBadge icon={p.icon} variant="navy" />
                <h3 className="mt-6 font-serif text-2xl font-600 text-navy">{p.title}</h3>
                <p className="mt-3 leading-relaxed text-ink">{p.text}</p>
              </article>
            </StaggerItem>
          ))}
        </StaggerGroup>
        <Reveal delay={0.1}>
          <p className="mt-10 font-serif text-3xl italic text-slate">There is a better way.</p>
        </Reveal>
      </Section>

      {/* OUR PROMISE (navy) */}
      <section className="relative overflow-hidden bg-navy py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="mb-14 flex items-start gap-5">
            <div className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-full border-4 border-gold sm:flex">
              <span className="h-4 w-4 rounded-full bg-gold" />
            </div>
            <div>
              <Reveal>
                <h2 className="font-serif text-4xl font-600 tracking-tight text-white md:text-5xl">Our promise</h2>
              </Reveal>
              <Reveal delay={0.06}>
                <p className="mt-4 max-w-2xl text-lg text-white/75">
                  A working website in four weeks — with fixed pricing and a clear, simple process.
                </p>
              </Reveal>
            </div>
          </div>
          <StaggerGroup className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {promises.map((p) => (
              <StaggerItem key={p.title}>
                <article className="h-full rounded-2xl border border-white/5 bg-white/[0.04] p-8 text-center transition-colors duration-300 hover:bg-white/[0.07]" data-testid={`promise-card-${p.title.split(" ")[0].toLowerCase()}`}>
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-slate/50 text-white">
                    <p.icon className="h-7 w-7" />
                  </div>
                  <h3 className="mt-6 font-serif text-xl font-600 text-white">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/70">{p.text}</p>
                </article>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* WHO WE BUILD FOR */}
      <Section className="bg-cream">
        <SectionHead eyebrow="Who we build for" title="We specialise — so we already understand your world." bird />
        <StaggerGroup className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {audiences.map((a) => (
            <StaggerItem key={a.title}>
              <Link
                to={a.to}
                data-testid={`audience-card-${a.title.toLowerCase()}`}
                className="group flex h-full flex-col rounded-2xl bg-white p-8 text-center shadow-soft transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-slate text-white transition-colors group-hover:bg-navy">
                  <a.icon className="h-7 w-7" />
                </div>
                <h3 className="mt-6 font-serif text-2xl font-600 text-navy">{a.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink">{a.text}</p>
                <span className="mt-5 inline-flex items-center justify-center gap-1 text-sm font-600 text-gold">
                  Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </Section>

      <EditorialMarquee />

      {/* FOUR WEEK TIMELINE */}
      <Section className="bg-cream">
        <SectionHead eyebrow="How it works" title="Your website in four weeks" sub="A clear, structured sprint from first brief to launch day." />
        <div className="relative">
          <div className="absolute left-[27px] top-2 bottom-2 hidden w-px bg-navy/15 md:block" />
          <StaggerGroup className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {steps.map((s) => (
              <StaggerItem key={s.n}>
                <div className="relative rounded-2xl bg-white p-8 shadow-soft" data-testid={`timeline-step-${s.n}`}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gold font-serif text-lg font-700 text-navy-dark">
                    {s.n}
                  </div>
                  <h3 className="mt-5 font-serif text-xl font-600 text-navy">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink">{s.text}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
        <Reveal delay={0.1}>
          <p className="mt-8 text-mutedink">
            Give us your content and feedback on time, and you could be live even sooner.
          </p>
        </Reveal>
      </Section>

      {/* MANIFESTO */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <SectionHead eyebrow="Our thinking" title="Rooted in insight. Built for tomorrow." />
          <div className="divide-y divide-navy/10 border-t border-navy/10">
            {manifesto.map((m) => (
              <Reveal key={m.n}>
                <div className="grid grid-cols-1 gap-4 py-10 md:grid-cols-12 md:gap-10">
                  <div className="md:col-span-3">
                    <span className="font-serif text-6xl font-700 text-gold md:text-7xl">{m.n}</span>
                  </div>
                  <div className="md:col-span-9">
                    <h3 className="font-serif text-2xl font-600 text-navy md:text-3xl">{m.title}</h3>
                    <p className="mt-3 max-w-2xl text-lg leading-relaxed text-ink">{m.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <Section className="bg-cream">
        <SectionHead eyebrow="Why choose us" title="Everything a growing Ghanaian business needs." bird />
        <StaggerGroup className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {reasons.map((r) => (
            <StaggerItem key={r.title}>
              <article className="flex h-full items-start gap-4 rounded-2xl bg-white p-7 shadow-soft" data-testid={`why-card-${r.title.split(" ")[0].toLowerCase()}`}>
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-navy/5 text-gold">
                  <r.icon className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-serif text-xl font-600 text-navy">{r.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-ink">{r.text}</p>
                </div>
              </article>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </Section>

      {/* TESTIMONIALS PLACEHOLDER */}
      <Section className="bg-white">
        <div className="rounded-2xl border border-dashed border-navy/25 bg-cream p-10 text-center md:p-16" data-testid="testimonials-placeholder">
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-gold">Testimonials</span>
          <h2 className="mt-3 font-serif text-3xl font-600 text-navy">Client stories coming soon</h2>
          <p className="mx-auto mt-3 max-w-xl text-mutedink">
            This space is reserved for real testimonials from our clients. We don't publish anything we haven't earned.
          </p>
        </div>
      </Section>

      <CtaBand />
    </>
  );
}
